import { useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { HttpError } from '@rossum/api-client/errors';
import { Inbox } from '@rossum/api-client/inboxes';
import { Queue } from '@rossum/api-client/queues';
import { ContentWrapper } from '@rossum/components/content-group/ContentWrapper';
import { SplitContent } from '@rossum/components/content-group/SplitContent';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@rossum/ui/material';
import { useTriageSetup } from '../../../../../business/deleteRecommendation/useTriageSetup';
import { QUERY_KEY_QUEUES_UNPAGINATED } from '../../../../../business/queues/useUnpaginatedQueues';
import HiddingButton from '../../../../../components/UI/HiddingButton';
import { EngineQueueSettings } from '../../../../../features/engines/components/EngineQueueSettings';
import {
  approvalWorkflowsFeatureSelector,
  documentArchiveFeatureSelector,
  hasSubscriptionPlanExistSelector,
} from '../../../../../features/pricing/selectors';
import { usePatchQueue } from '../../../../../features/queues/hooks/usePatchQueue';
import {
  WorkflowQueueSettings,
  workflowsSchema,
} from '../../../../../features/workflows';
import {
  auroraEngineConfigEnabledSelector,
  memoryFieldsEnabledSelector,
  restrictedEngineEditEnabledSelector,
} from '../../../../../redux/modules/organizationGroup/selectors';
import TriageConditionsDrawer from '../../../../TriageConditionsDrawer/TriageConditionsDrawer';
import {
  getValidDocumentLifetime,
  isSplitBatchFilesEnabled,
  setValidDocumentLifetime,
} from '../helpers';
import Advanced from './Advanced';
import ConfirmState from './ConfirmState';
import Document from './Document';
import DocumentArchive from './DocumentArchive';
import { MemoryLearningMode, QueueSettingsData } from './formType';
import General from './General';
import Notifications from './Notifications';

type QueueSettingsProps = {
  selectedQueue: Queue;
  inbox: Inbox | undefined;
};

const queueSettingsValidationSchema = (intl: IntlShape) =>
  yup.object().shape({
    locale: yup.string(),
    name: yup.string().required(
      intl.formatMessage({
        id: 'containers.settings.queues.messages.name.required',
      })
    ),
    sessionTimeoutHours: yup.string().matches(
      /^(\d{1,2})$/,
      intl.formatMessage({
        id: 'containers.settings.queues.messages.sessionTimeoutHours.errorThreshhold',
      })
    ),
    sessionTimeoutMinutes: yup.string().matches(
      /^(\d{1,2})$/,
      intl.formatMessage({
        id: 'containers.settings.queues.messages.sessionTimeoutMinutes.errorThreshhold',
      })
    ),
    sessionTimeoutSeconds: yup.string().matches(
      /^(\d{1,2})$/,
      intl.formatMessage({
        id: 'containers.settings.queues.messages.sessionTimeoutSeconds.errorThreshhold',
      })
    ),
    allowUploads: yup.boolean(),
    useConfirmedState: yup.boolean(),
    hideExportButton: yup.boolean(),
    suggestedEdit: yup.string(),
    deletedAnnotations: yup.boolean(),
    postponedAnnotations: yup.boolean(),
    unprocessableAttachments: yup.boolean(),
    archiveEnabled: yup.boolean(),
    emailTo: yup
      .string()
      .nullable()
      .email(
        intl.formatMessage({
          id: 'containers.settings.queues.messages.emailTo.email',
        })
      ),
    workflows: yup.object(),
    engineGroup: yup.object().shape({
      engine: yup
        .string()
        .when('$engineEnabled', (value: boolean, schema: yup.StringSchema) => {
          if (value) {
            return schema.required();
          }

          return schema.nullable();
        }),
    }),
    documentLifetime: yup
      .string()
      .nullable()
      .matches(
        /^\d*$/,
        intl.formatMessage({
          id: 'containers.settings.queues.documentLifetime.error.numeric',
        })
      )
      .test(
        'max-value',
        intl.formatMessage({
          id: 'containers.settings.queues.documentLifetime.error.maxValue',
        }),
        value => {
          if (!value) return true; // Allow empty/null values
          const numValue = parseInt(value, 10);
          return numValue <= 999999999;
        }
      ),
  });

export const QueueBasicSettingsContainer = ({
  selectedQueue,
  inbox,
}: QueueSettingsProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const intl = useIntl();
  const queryClient = useQueryClient();

  const { id: queueId, settings: selectedQueueSettings } = selectedQueue;
  const { mutateAsync: patchQueue, error } = usePatchQueue();

  const [sessionTimeoutH, sessionTimeoutM, sessionTimeoutS] =
    selectedQueue.sessionTimeout.split(':');

  const { isReady, setTriageEnabled, isTriageEnabled, triggerUrl } =
    useTriageSetup(selectedQueue.url);

  const defaultValues: QueueSettingsData = {
    locale: selectedQueue.locale,
    name: selectedQueue.name,
    sessionTimeoutHours: sessionTimeoutH,
    sessionTimeoutMinutes: sessionTimeoutM,
    sessionTimeoutSeconds: sessionTimeoutS,
    allowUploads: selectedQueue.settings?.uiUploadEnabled ?? true,
    useConfirmedState: selectedQueue.useConfirmedState,
    hideExportButton: !!selectedQueueSettings.hideExportButton,
    suggestedEdit: selectedQueueSettings.suggestedEdit,
    deletedAnnotations:
      selectedQueueSettings.emailNotifications?.deletedAnnotations,
    postponedAnnotations:
      selectedQueueSettings.emailNotifications?.postponedAnnotations,
    unprocessableAttachments:
      selectedQueueSettings.emailNotifications?.unprocessableAttachments,
    emailTo: selectedQueueSettings.emailNotifications?.recipient?.email || '',
    triageStateEnabled: isTriageEnabled,
    workflowGroup: {
      enabled: selectedQueueSettings.workflows?.enabled ?? false,
      bypassWorkflowsAllowed:
        selectedQueueSettings.workflows?.bypassWorkflowsAllowed ?? false,
      workflows: workflowsSchema.catch([]).parse(selectedQueue.workflows),
    },
    documentLifetime:
      getValidDocumentLifetime(selectedQueue.documentLifetime) ?? '',
    engineGroup: {
      engine: selectedQueue.engine,
    },
    memoryLearningMode: selectedQueueSettings.memoryLearningMode ?? 'automatic',
    archiveEnabled: selectedQueue.archiveEnabled,
  };

  const isEngineEditRestrictedEnabled = useSelector(
    restrictedEngineEditEnabledSelector
  );

  const auroraEngineConfigEnabled = useSelector(
    auroraEngineConfigEnabledSelector
  );
  const memoryFieldsEnabled = useSelector(memoryFieldsEnabledSelector);
  const queueEngine = selectedQueue.engine;
  const showEngineConfiguration = !!(queueEngine && auroraEngineConfigEnabled);

  const {
    watch,
    handleSubmit,
    formState: { isValid, errors },
    control,
    setFocus,
    setError,
    setValue,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(queueSettingsValidationSchema(intl)),
    context: {
      engineEnabled: showEngineConfiguration,
    },
  });

  const isEngineError = useMemo(() => {
    const httpError = error as HttpError;

    if (httpError && 'data' in httpError && R.isObjectType(httpError.data))
      return Object.keys(httpError.data).includes('non_field_errors');

    return false;
  }, [error]);

  useEffect(() => {
    if (isEngineError) {
      setError('engineGroup.engine', {
        message: intl.formatMessage({
          id: 'features.engines.queueSettings.error',
        }),
      });
    }
  }, [intl, isEngineError, setError]);

  useEffect(() => {
    setValue('triageStateEnabled', isTriageEnabled);
  }, [isTriageEnabled, setValue]);

  const watchValues = watch();

  const suggestedEditEnabled = isSplitBatchFilesEnabled(selectedQueue);

  const canSubmit = !R.isDeepEqual(watchValues, defaultValues) && isValid;

  const onSave = ({
    locale,
    name,
    sessionTimeoutHours,
    sessionTimeoutMinutes,
    sessionTimeoutSeconds,
    allowUploads,
    useConfirmedState,
    hideExportButton,
    suggestedEdit,
    deletedAnnotations,
    postponedAnnotations,
    unprocessableAttachments,
    emailTo,
    triageStateEnabled,
    workflowGroup,
    engineGroup,
    archiveEnabled,
    documentLifetime,
    memoryLearningMode,
  }: QueueSettingsData) => {
    if (
      triageStateEnabled !== undefined &&
      triageStateEnabled !== isTriageEnabled
    ) {
      setTriageEnabled(triageStateEnabled);
    }

    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_QUEUES_UNPAGINATED],
    });

    patchQueue({
      queueId,
      payload: {
        locale,
        name,
        sessionTimeout: [
          sessionTimeoutHours,
          sessionTimeoutMinutes,
          sessionTimeoutSeconds,
        ].join(':'),
        useConfirmedState,
        archiveEnabled,
        documentLifetime: setValidDocumentLifetime(documentLifetime),
        settings: {
          ...selectedQueueSettings,
          uiUploadEnabled: allowUploads,
          hideExportButton,
          suggestedEdit,
          emailNotifications: {
            recipient: emailTo ? { email: emailTo } : null,
            deletedAnnotations,
            unprocessableAttachments,
            postponedAnnotations,
          },
          workflows: workflowGroup
            ? {
                enabled: workflowGroup.enabled,
                bypassWorkflowsAllowed: workflowGroup.bypassWorkflowsAllowed,
              }
            : undefined,
          memoryLearningMode,
        },
        workflows: workflowGroup ? workflowGroup.workflows : undefined,
        engine: engineGroup.engine,
      },
    });
  };

  const location = useLocation<{ openTriageRules: boolean }>();

  useEffect(() => {
    if (location.state?.openTriageRules) setDrawerOpen(true);
  }, [location.state]);

  const workflowsEnabled = useSelector(approvalWorkflowsFeatureSelector);

  const documentArchivePurchasedEnabled = useSelector(
    documentArchiveFeatureSelector
  );
  const hasSubscriptionPlan = useSelector(hasSubscriptionPlanExistSelector);

  return (
    <>
      <form onSubmit={handleSubmit(onSave)}>
        <Stack data-page-title="queue-settings" gap={4}>
          <General
            id={selectedQueue.id}
            inbox={inbox?.email}
            control={control}
            setFocus={setFocus}
          />
          <Document
            suggestedEditEnabled={suggestedEditEnabled}
            openTriageSettings={() => setDrawerOpen(true)}
            control={control}
            isTriageReady={isReady}
            errors={errors}
            initialDocumentLifetimeValue={selectedQueue.documentLifetime}
          />
          {hasSubscriptionPlan ? (
            <DocumentArchive
              control={control}
              disabled={!documentArchivePurchasedEnabled}
            />
          ) : null}
          <Notifications control={control} intl={intl} />
          <ConfirmState
            control={control}
            useConfirmedState={watchValues.useConfirmedState ?? false}
          />
          <Advanced inboxExists={inbox !== undefined} />
          {workflowsEnabled && (
            <Controller
              control={control}
              name="workflowGroup"
              render={({ field }) =>
                field.value !== undefined ? (
                  <WorkflowQueueSettings
                    value={field.value}
                    onChange={field.onChange}
                  />
                ) : (
                  // TODO: This is probably an evil pattern overall, likely don't render entire Controller
                  // oxlint-disable-next-line react/jsx-no-useless-fragment
                  <></>
                )
              }
            />
          )}
          {showEngineConfiguration ? (
            <Controller
              control={control}
              name="engineGroup"
              render={({ field, formState }) => (
                <EngineQueueSettings
                  {...field}
                  disabled={isEngineEditRestrictedEnabled}
                  queueEngine={queueEngine}
                  error={formState.errors.engineGroup?.engine}
                />
              )}
            />
          ) : null}
          {memoryFieldsEnabled && (
            <ContentWrapper>
              <SplitContent
                title={intl.formatMessage({
                  id: 'features.engines.queueSettings.memoryLearningMode.title',
                })}
                description={intl.formatMessage({
                  id: 'features.engines.queueSettings.memoryLearningMode.description',
                })}
              >
                <Controller
                  control={control}
                  name="memoryLearningMode"
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ width: '400px', maxWidth: '100%' }}
                    >
                      <InputLabel id="memory-learning-mode-label">
                        {intl.formatMessage({
                          id: 'features.engines.queueSettings.memoryLearningMode.label',
                        })}
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="memory-learning-mode-label"
                        value={field.value ?? 'automatic'}
                        label={intl.formatMessage({
                          id: 'features.engines.queueSettings.memoryLearningMode.label',
                        })}
                        onChange={e =>
                          field.onChange(e.target.value as MemoryLearningMode)
                        }
                      >
                        <MenuItem value="automatic">
                          {intl.formatMessage({
                            id: 'features.engines.queueSettings.memoryLearningMode.automatic',
                          })}
                        </MenuItem>
                        <MenuItem value="manual">
                          {intl.formatMessage({
                            id: 'features.engines.queueSettings.memoryLearningMode.manual',
                          })}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </SplitContent>
            </ContentWrapper>
          )}
          <Stack
            direction="row"
            sx={{ justifyContent: 'center', mt: '50px', mb: '21px' }}
          >
            <HiddingButton
              hideCondition={!canSubmit}
              messageId="containers.settings.queues.save"
              type="submit"
              dataCy="queue-settings-save-changes-btn"
            />
          </Stack>
        </Stack>
      </form>
      <TriageConditionsDrawer
        queueUrl={selectedQueue.url}
        schemaUrl={selectedQueue.schema}
        triggerUrl={triggerUrl}
        DrawerProps={{
          open: drawerOpen,
          onClose: () => setDrawerOpen(false),
        }}
      />
    </>
  );
};
